import {
  hasPermission,
  installedListSchema,
  installerChangeResultSchema,
  installerDependencySchema,
  installerProjectSchema,
  installerSearchResultSchema,
  okSchema,
  type InstalledEntry,
  type InstallerChangeResult,
  type InstallerContext,
  type InstallerProjectSummary,
  type InstallerSource,
} from '@hearth/shared';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowUpCircle,
  CircleOff,
  Download,
  ExternalLink,
  Loader2,
  PackageOpen,
  Puzzle,
  RotateCw,
  Search,
  Trash2,
  Unplug,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Confirm } from '@/components/confirm';
import { EmptyState } from '@/components/page-header';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiRequest } from '@/lib/api';
import { bytes, timeAgo } from '@/lib/format';
import { errorMessage } from '@/lib/forms';
import { installerContextQuery } from '@/lib/installer-queries';
import { serverQuery } from '@/lib/server-queries';
import { useServerLive } from '@/lib/server-socket';

export const Route = createFileRoute('/_app/server/$server/plugins')({ component: PluginsTab });

const base = (server: string) => `/api/client/servers/${server}/installer`;

const installedQuery = (server: string) =>
  queryOptions({
    queryKey: ['server', server, 'installer', 'installed'],
    queryFn: ({ signal }) =>
      apiRequest(`${base(server)}/installed`, { schema: installedListSchema, signal }),
  });

const SOURCE_LABEL: Record<InstallerSource, string> = {
  modrinth: 'Modrinth',
  curseforge: 'CurseForge',
};
const LOADER_LABEL: Record<string, string> = {
  paper: 'Paper',
  spigot: 'Spigot',
  bukkit: 'Bukkit',
  purpur: 'Purpur',
  folia: 'Folia',
  fabric: 'Fabric',
  forge: 'Forge',
  neoforge: 'NeoForge',
  quilt: 'Quilt',
};
const loaderName = (l: string) => LOADER_LABEL[l] ?? l;

function PluginsTab() {
  const { server: id } = Route.useParams();
  const context = useQuery(installerContextQuery(id));
  const [restart, setRestart] = useState(false);

  if (context.isPending)
    return (
      <div className="grid gap-3" aria-busy>
        <Skeleton className="h-10 w-64 rounded-lg" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  if (context.isError)
    return (
      <Alert variant="destructive">
        <Unplug />
        <AlertTitle>Couldn’t load the installer</AlertTitle>
        <AlertDescription>{errorMessage(context.error)}</AlertDescription>
      </Alert>
    );
  const ctx = context.data;
  if (!ctx.supported || !ctx.kind)
    return (
      <EmptyState icon={CircleOff} title="No plugin or mod support for this server">
        {ctx.curseforge === 'not_configured' && ctx.kind
          ? 'This server only offers CurseForge, which isn’t set up on this panel. An admin can add an API key in Settings → Integrations.'
          : 'Hearth installs Paper, Spigot and Purpur plugins and Fabric, Forge, NeoForge and Quilt mods. This server’s software doesn’t load either, so there is nothing to install here.'}
      </EmptyState>
    );
  const noun = ctx.kind === 'plugin' ? 'plugins' : 'mods';

  return (
    <div className="grid gap-4">
      <p className="text-muted-foreground text-sm">
        {ctx.kind === 'plugin' ? 'Plugins' : 'Mods'} for{' '}
        {ctx.sources[0]?.loaders.map(loaderName).join(' / ')}
        {ctx.gameVersion ? ` · Minecraft ${ctx.gameVersion}` : ''}, installed in{' '}
        <code className="text-foreground">{ctx.folder}</code>. Modpacks aren’t supported yet: Hearth
        installs single {noun}.
      </p>
      {!ctx.gameVersion && (
        <Alert>
          <PackageOpen />
          <AlertTitle>Versions are matched by loader only</AlertTitle>
          <AlertDescription>
            {ctx.gameVersionRaw
              ? `The server’s Minecraft version is set to “${ctx.gameVersionRaw}”, so Hearth can’t tell which game version the ${noun} must support.`
              : `Hearth can’t tell this server’s Minecraft version.`}{' '}
            Set a specific version in Startup to filter by it.
          </AlertDescription>
        </Alert>
      )}
      {restart && <RestartPrompt server={id} noun={noun} onDone={() => setRestart(false)} />}
      <Tabs defaultValue="installed">
        <TabsList>
          <TabsTrigger value="installed">Installed</TabsTrigger>
          <TabsTrigger value="browse">Browse</TabsTrigger>
        </TabsList>
        <TabsContent value="installed">
          <InstalledPane server={id} noun={noun} onChanged={() => setRestart(true)} />
        </TabsContent>
        <TabsContent value="browse">
          <BrowsePane server={id} context={ctx} onChanged={() => setRestart(true)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** Shown after any change: the game only loads plugins and mods when it starts. */
function RestartPrompt({
  server,
  noun,
  onDone,
}: {
  server: string;
  noun: string;
  onDone: () => void;
}) {
  const { data: s } = useSuspenseQuery(serverQuery(server));
  const live = useServerLive(server);
  const running = live.state === 'running' || live.state === 'starting';
  const canRestart = hasPermission(s.permissions, 'control.restart');
  const restart = useMutation({
    mutationFn: () =>
      apiRequest(`/api/client/servers/${server}/power`, {
        method: 'POST',
        body: { action: 'restart' },
        schema: okSchema,
      }),
    onSuccess: () => {
      toast.success('Restarting…');
      onDone();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!running)
    return (
      <Alert>
        <RotateCw />
        <AlertTitle>Changes apply at the next start</AlertTitle>
        <AlertDescription>The server is offline; the {noun} load when it starts.</AlertDescription>
      </Alert>
    );
  return (
    <Alert>
      <RotateCw />
      <AlertTitle>Restart to apply your changes</AlertTitle>
      <AlertDescription className="grid gap-3">
        <p>The game only loads {noun} when it starts. Nothing restarts until you choose to.</p>
        <div className="flex flex-wrap gap-2">
          {canRestart && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => restart.mutate()}
              disabled={restart.isPending}
            >
              {restart.isPending ? <Loader2 className="animate-spin" /> : <RotateCw />} Restart now
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDone}>
            Later
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}

function ProjectIcon({ url, title }: { url: string | null; title: string }) {
  const [failed, setFailed] = useState(false);
  if (url && !failed)
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className="bg-elevated size-10 shrink-0 rounded-md border object-cover"
      />
    );
  return (
    <span
      aria-hidden
      className="bg-game-soft text-game grid size-10 shrink-0 place-items-center rounded-md text-sm font-semibold uppercase"
    >
      {title.replace(/[^A-Za-z0-9]/g, '').slice(0, 2) || '?'}
    </span>
  );
}

function useChangeHandler(server: string, onChanged: () => void) {
  const queryClient = useQueryClient();
  return (r: InstallerChangeResult) => {
    void queryClient.invalidateQueries({ queryKey: installedQuery(server).queryKey });
    for (const s of r.skipped) toast.warning(`${s.title} wasn’t installed: ${s.reason}`);
    if (r.restartRequired) onChanged();
  };
}

function InstalledPane({
  server,
  noun,
  onChanged,
}: {
  server: string;
  noun: string;
  onChanged: () => void;
}) {
  const { data: s } = useSuspenseQuery(serverQuery(server));
  const list = useQuery(installedQuery(server));
  const changed = useChangeHandler(server, onChanged);
  const can = (p: Parameters<typeof hasPermission>[1]) => hasPermission(s.permissions, p);
  const update = useMutation({
    mutationFn: (file: string) =>
      apiRequest(`${base(server)}/installed/${encodeURIComponent(file)}/update`, {
        method: 'POST',
        schema: installerChangeResultSchema,
      }),
    onSuccess: (r) => {
      toast.success(`Updated to ${r.installed[0]?.versionNumber ?? 'the newest version'}.`);
      changed(r);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (file: string) =>
      apiRequest(`${base(server)}/installed/${encodeURIComponent(file)}`, {
        method: 'DELETE',
        schema: installerChangeResultSchema,
      }),
    onSuccess: (r) => {
      toast.success(`Removed ${r.removed[0]}.`);
      changed(r);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (list.isPending) return <Skeleton className="h-40 rounded-xl" aria-busy />;
  if (list.isError)
    return (
      <Alert variant="destructive">
        <Unplug />
        <AlertTitle>Couldn’t read the folder</AlertTitle>
        <AlertDescription>{errorMessage(list.error)}</AlertDescription>
      </Alert>
    );
  const data = list.data;
  if (!data.entries.length)
    return (
      <EmptyState icon={Puzzle} title={`No ${noun} installed`}>
        {data.exists
          ? `${data.folder} has no .jar files yet.`
          : `${data.folder} doesn’t exist yet; it is created with the first install.`}{' '}
        Find some under Browse.
      </EmptyState>
    );
  const busy = update.isPending || remove.isPending;
  return (
    <div className="grid gap-3">
      {data.updates === 'failed' && (
        <p className="text-muted-foreground text-sm">
          Couldn’t check for updates right now; the provider didn’t answer.
        </p>
      )}
      <Card className="gap-0 overflow-hidden p-0">
        <ul className="divide-y">
          {data.entries.map((e) => (
            <InstalledRow
              key={e.file}
              entry={e}
              busy={busy}
              updating={update.isPending && update.variables === e.file}
              canUpdate={can('file.create') && can('file.delete')}
              canRemove={can('file.delete')}
              onUpdate={() => update.mutate(e.file)}
              onRemove={() => remove.mutate(e.file)}
            />
          ))}
        </ul>
      </Card>
      <p className="text-muted-foreground text-xs">
        Jars added by hand show as “Added manually”; Hearth can remove them but can’t update them.
      </p>
    </div>
  );
}

function InstalledRow({
  entry: e,
  busy,
  updating,
  canUpdate,
  canRemove,
  onUpdate,
  onRemove,
}: {
  entry: InstalledEntry;
  busy: boolean;
  updating: boolean;
  canUpdate: boolean;
  canRemove: boolean;
  onUpdate: () => void;
  onRemove: () => void;
}) {
  const title = e.managed?.title ?? e.file;
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <ProjectIcon url={e.managed?.iconUrl ?? null} title={title} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{title}</p>
        <p className="text-muted-foreground truncate text-xs">
          {e.managed
            ? `${e.managed.versionNumber} · ${SOURCE_LABEL[e.managed.source]} · ${e.file}`
            : `${e.file} · ${bytes(e.size)}`}
        </p>
      </div>
      {!e.managed && <Badge variant="outline">Added manually</Badge>}
      {e.update && <Badge variant="info">{e.update.versionNumber} available</Badge>}
      <div className="flex gap-1">
        {e.update && canUpdate && (
          <Button size="sm" variant="outline" onClick={onUpdate} disabled={busy}>
            {updating ? <Loader2 className="animate-spin" /> : <ArrowUpCircle />} Update
          </Button>
        )}
        {canRemove && (
          <Confirm
            title={`Remove ${title}?`}
            description={`This deletes ${e.file} from the server. It stays loaded until the server restarts.`}
            action="Remove"
            destructive
            onConfirm={onRemove}
          >
            <Button size="icon-sm" variant="ghost" aria-label={`Remove ${title}`} disabled={busy}>
              <Trash2 />
            </Button>
          </Confirm>
        )}
      </div>
    </li>
  );
}

function BrowsePane({
  server,
  context,
  onChanged,
}: {
  server: string;
  context: InstallerContext;
  onChanged: () => void;
}) {
  const { data: s } = useSuspenseQuery(serverQuery(server));
  const canInstall = hasPermission(s.permissions, 'file.create');
  const [source, setSource] = useState<InstallerSource>(context.sources[0]!.source);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<InstallerProjectSummary | null>(null);
  // Search as the user types, but not on every keystroke (provider quota is shared).
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(text.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [text]);
  const results = useQuery({
    queryKey: ['server', server, 'installer', 'search', source, query, page],
    queryFn: ({ signal }) =>
      apiRequest(
        `${base(server)}/search?${new URLSearchParams({ source, query, page: String(page) })}`,
        { schema: installerSearchResultSchema, signal },
      ),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const pages = results.data
    ? Math.max(1, Math.ceil(results.data.total / results.data.pageSize))
    : 1;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search className="text-subtle pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            className="pl-8"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Search ${SOURCE_LABEL[source]}`}
            aria-label="Search"
            maxLength={100}
          />
        </div>
        {context.sources.length > 1 && (
          <Select
            value={source}
            onValueChange={(v) => {
              setSource(v as InstallerSource);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-40" aria-label="Source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {context.sources.map((src) => (
                <SelectItem key={src.source} value={src.source}>
                  {SOURCE_LABEL[src.source]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {context.curseforge === 'not_configured' && (
        <p className="text-muted-foreground text-xs">
          CurseForge is available once an admin adds an API key in Settings → Integrations.
        </p>
      )}

      {results.isPending ? (
        <div className="grid gap-3 sm:grid-cols-2" aria-busy>
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : results.isError ? (
        <Alert variant="destructive">
          <Unplug />
          <AlertTitle>Search failed</AlertTitle>
          <AlertDescription>{errorMessage(results.error)}</AlertDescription>
        </Alert>
      ) : results.data.hits.length === 0 ? (
        <EmptyState icon={Search} title="Nothing found">
          {query ? `No results for “${query}” that fit this server.` : 'No results.'}
        </EmptyState>
      ) : (
        <>
          <ul className="grid gap-3 sm:grid-cols-2" aria-busy={results.isFetching}>
            {results.data.hits.map((p) => (
              <li key={`${p.source}:${p.id}`}>
                <Card className="h-full flex-row items-start gap-3 px-4 py-3">
                  <ProjectIcon url={p.iconUrl} title={p.title} />
                  <div className="grid min-w-0 flex-1 gap-1">
                    <div className="flex items-baseline gap-2">
                      <p className="truncate font-medium">{p.title}</p>
                      {p.author && (
                        <p className="text-muted-foreground truncate text-xs">by {p.author}</p>
                      )}
                    </div>
                    <p className="text-muted-foreground line-clamp-2 text-sm">{p.description}</p>
                    <div className="text-subtle flex flex-wrap items-center gap-x-3 text-xs">
                      <span>{p.downloads.toLocaleString()} downloads</span>
                      {p.updatedAt && <span>updated {timeAgo(p.updatedAt)}</span>}
                      <a
                        href={p.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="hover:text-foreground inline-flex items-center gap-1"
                      >
                        {SOURCE_LABEL[p.source]} <ExternalLink className="size-3" />
                      </a>
                    </div>
                  </div>
                  {canInstall && (
                    <Button size="sm" variant="outline" onClick={() => setPicked(p)}>
                      <Download /> Install
                    </Button>
                  )}
                </Card>
              </li>
            ))}
          </ul>
          {pages > 1 && (
            <div className="flex items-center justify-end gap-2 text-sm">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-muted-foreground tabular-nums">
                Page {page} of {pages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= Math.min(pages, 50)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
      {picked && (
        <InstallDialog
          server={server}
          project={picked}
          onClose={() => setPicked(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}

function InstallDialog({
  server,
  project,
  onClose,
  onChanged,
}: {
  server: string;
  project: InstallerProjectSummary;
  onClose: () => void;
  onChanged: () => void;
}) {
  const changed = useChangeHandler(server, onChanged);
  const projectUrl = `${base(server)}/projects/${project.source}/${encodeURIComponent(project.id)}`;
  const detail = useQuery({
    queryKey: ['server', server, 'installer', 'project', project.source, project.id],
    queryFn: ({ signal }) => apiRequest(projectUrl, { schema: installerProjectSchema, signal }),
  });
  const versions = detail.data?.versions ?? [];
  const firstFit =
    versions.find((v) => v.compatible && v.type === 'release') ??
    versions.find((v) => v.compatible);
  const [versionId, setVersionId] = useState<string | null>(null);
  const chosen = versionId ?? firstFit?.id ?? null;
  const version = versions.find((v) => v.id === chosen);

  const deps = useQuery({
    queryKey: ['server', server, 'installer', 'deps', project.source, project.id, chosen],
    enabled: Boolean(version?.compatible && version.dependencies.length),
    queryFn: ({ signal }) =>
      apiRequest(`${projectUrl}/versions/${encodeURIComponent(chosen!)}/dependencies`, {
        schema: z.object({ data: z.array(installerDependencySchema) }),
        signal,
      }).then((r) => r.data),
  });
  // Consent per dependency: required ones start ticked, optional ones don't.
  const [declined, setDeclined] = useState<Set<string>>(new Set());
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const installable = (deps.data ?? []).filter((d) => !d.installed && d.version);
  const selected = installable
    .filter((d) =>
      d.type === 'required' ? !declined.has(d.project.id) : accepted.has(d.project.id),
    )
    .map((d) => d.project.id);
  const toggle = (id: string, required: boolean, on: boolean) => {
    const update = (set: Set<string>, add: boolean) => {
      const next = new Set(set);
      if (add) next.add(id);
      else next.delete(id);
      return next;
    };
    if (required) setDeclined((s) => update(s, !on));
    else setAccepted((s) => update(s, on));
  };

  const install = useMutation({
    mutationFn: () =>
      apiRequest(`${base(server)}/install`, {
        method: 'POST',
        body: {
          source: project.source,
          projectId: project.id,
          versionId: chosen,
          dependencies: selected,
        },
        schema: installerChangeResultSchema,
      }),
    onSuccess: (r) => {
      toast.success(
        r.installed.length > 1
          ? `Installed ${r.installed.map((i) => i.title).join(', ')}.`
          : `Installed ${project.title}.`,
      );
      changed(r);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const skippedRequired = (deps.data ?? []).filter(
    (d) => d.type === 'required' && !d.installed && (!d.version || declined.has(d.project.id)),
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Install {project.title}</DialogTitle>
          <DialogDescription>
            From {SOURCE_LABEL[project.source]}. Hearth’s node downloads the file and checks its
            hash before saving it.
          </DialogDescription>
        </DialogHeader>
        {detail.isPending ? (
          <Skeleton className="h-24 rounded-lg" aria-busy />
        ) : detail.isError ? (
          <Alert variant="destructive">
            <Unplug />
            <AlertTitle>Couldn’t load versions</AlertTitle>
            <AlertDescription>{errorMessage(detail.error)}</AlertDescription>
          </Alert>
        ) : !versions.length ? (
          <p className="text-muted-foreground text-sm">
            {project.title} has no versions for this server’s software.
          </p>
        ) : (
          <div className="grid gap-4">
            <div className="grid gap-1.5">
              <label className="text-sm font-medium" htmlFor="installer-version">
                Version
              </label>
              <Select value={chosen ?? undefined} onValueChange={setVersionId}>
                <SelectTrigger id="installer-version" className="w-full">
                  <SelectValue placeholder="No compatible version" />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((v) => (
                    <SelectItem key={v.id} value={v.id} disabled={!v.compatible}>
                      <span className="truncate">{v.versionNumber}</span>
                      {v.type !== 'release' && (
                        <span className="text-warning text-xs">{v.type}</span>
                      )}
                      {!v.compatible && (
                        <span className="text-muted-foreground text-xs">
                          {v.incompatibleReason}
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {version && (
                <p className="text-muted-foreground text-xs">
                  {version.file?.name}
                  {version.file ? ` · ${bytes(version.file.size)}` : ''} · for{' '}
                  {version.loaders.map(loaderName).join(', ')}
                  {version.gameVersions.length
                    ? ` · Minecraft ${version.gameVersions.slice(-3).join(', ')}`
                    : ''}
                </p>
              )}
              {!firstFit && (
                <p className="text-destructive text-sm">
                  No version fits this server’s software and Minecraft version.
                </p>
              )}
            </div>
            {version?.compatible && version.dependencies.length > 0 && (
              <fieldset className="grid gap-2">
                <legend className="mb-1 text-sm font-medium">Dependencies</legend>
                {deps.isPending ? (
                  <Skeleton className="h-10 rounded-md" />
                ) : deps.isError ? (
                  <p className="text-destructive text-sm">{errorMessage(deps.error)}</p>
                ) : (
                  deps.data.map((d) => {
                    const disabled = d.installed || !d.version;
                    const checked = selected.includes(d.project.id);
                    return (
                      <label
                        key={d.project.id}
                        className="flex items-start gap-2 text-sm has-[:disabled]:opacity-70"
                      >
                        <Checkbox
                          checked={d.installed || checked}
                          disabled={disabled}
                          onCheckedChange={(on) =>
                            toggle(d.project.id, d.type === 'required', on === true)
                          }
                          className="mt-0.5"
                        />
                        <span className="grid gap-0.5">
                          <span>
                            {d.project.title}{' '}
                            <span className="text-muted-foreground text-xs">
                              {d.type === 'required' ? 'required' : 'optional'}
                            </span>
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {d.installed
                              ? 'Already installed'
                              : d.version
                                ? `Installs ${d.version.versionNumber}`
                                : 'No version fits this server'}
                          </span>
                        </span>
                      </label>
                    );
                  })
                )}
              </fieldset>
            )}
            {skippedRequired.length > 0 && (
              <p className="text-warning text-sm">
                Without {skippedRequired.map((d) => d.project.title).join(', ')}, {project.title}{' '}
                may fail to load.
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => install.mutate()}
            disabled={!version?.compatible || install.isPending || deps.isFetching}
          >
            {install.isPending ? <Loader2 className="animate-spin" /> : <Download />}
            {selected.length ? `Install with ${selected.length} more` : 'Install'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
